/**
 * Creates a UUID for browser-side correlation identifiers.
 *
 * `crypto.randomUUID()` is restricted to secure contexts, so it is not
 * available when the site is opened over a LAN HTTP address during local
 * development. `getRandomValues()` remains available there and provides the
 * same UUID version/variant contract for these non-secret identifiers.
 */
export const createClientUuid = (): string => {
  const cryptoApi = typeof globalThis.crypto !== "undefined" ? globalThis.crypto : undefined;

  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  if (typeof cryptoApi?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last-resort fallback for older browsers without Web Crypto support.
  return `client-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};
